import React from "react";
import { toast } from "sonner";
import { CloudArrowUp, CheckCircle, Confetti, Money, Bell } from "@phosphor-icons/react";

/**
 * v1.25.44 — Animated success toasts for the slip-upload → approve → notify flow.
 * All variants use Phosphor icons + tailwind `animate-*` classes so no extra deps.
 */

const Base = ({ icon: Icon, tint, animation, title, sub }) => (
    <div className="flex items-start gap-3 py-1">
        <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${tint} ${animation}`}>
            <Icon size={20} weight="fill" className="text-white" />
        </div>
        <div className="min-w-0">
            <div className="text-sm font-semibold text-slate-900">{title}</div>
            {sub && <div className="mt-0.5 text-xs text-slate-500">{sub}</div>}
        </div>
    </div>
);

/** Slip has been uploaded — before verification. */
export const toastSlipUploaded = (count = 1, extraSub = "") => {
    toast.custom(
        () => (
            <Base
                icon={CloudArrowUp}
                tint="bg-gradient-to-br from-blue-500 to-indigo-600 shadow-lg shadow-indigo-200"
                animation="animate-bounce"
                title={`${count} slip${count > 1 ? "s" : ""} uploaded`}
                sub={extraSub || "Awaiting admin verification…"}
            />
        ),
        { duration: 3500 }
    );
};

/** Admin has approved a slip. */
export const toastSlipApproved = (amount) => {
    toast.custom(
        () => (
            <div className="flex items-start gap-3 py-1">
                <div className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-emerald-500 to-green-600 shadow-lg shadow-emerald-200 animate-[ping_0.7s_ease-out_1]">
                    <CheckCircle size={20} weight="fill" className="text-white" />
                </div>
                <div className="min-w-0">
                    <div className="text-sm font-semibold text-emerald-900">Slip approved</div>
                    {amount && <div className="mt-0.5 text-xs text-slate-600">{amount} added to the party ledger</div>}
                </div>
                <Confetti size={18} weight="fill" className="text-amber-400 animate-pulse" />
            </div>
        ),
        { duration: 3500 }
    );
};

/** Party sees their payment landed (fires on customer dashboard). */
export const toastPaymentReceived = (amount) => {
    toast.custom(
        () => (
            <div className="flex items-start gap-3 py-1">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 shadow-lg shadow-teal-200 animate-pulse">
                    <Money size={20} weight="fill" className="text-white" />
                </div>
                <div className="min-w-0">
                    <div className="text-sm font-semibold text-emerald-900">Payment approved 🎉</div>
                    <div className="mt-0.5 text-xs text-slate-600">{amount} has been credited to your ledger</div>
                </div>
                <Bell size={18} weight="fill" className="text-amber-400 animate-bounce" />
            </div>
        ),
        { duration: 5000 }
    );
};
